import { get, post, patch, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse, PaginationMeta } from '../../types/api';
import { Patient, CreatePatientDto, UpdatePatientDto, DuplicateCheckParams } from '../../types/patient';

export interface PatientListFilters {
  search?: string;
  gender?: string;
  bloodGroup?: string;
  isActive?: boolean;
}

export const patientService = {
  getPatients: (page = 1, limit = 20, filters?: PatientListFilters): Promise<ApiResponse<Patient[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.gender) params.set('gender', filters.gender);
    if (filters?.bloodGroup) params.set('bloodGroup', filters.bloodGroup);
    if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
    return get<Patient[]>(`/patients?${params.toString()}`) as Promise<ApiResponse<Patient[]> & { meta: PaginationMeta }>;
  },

  getPatient: (id: string): Promise<ApiResponse<Patient>> => {
    return get<Patient>(`/patients/${id}`);
  },

  createPatient: (data: CreatePatientDto): Promise<ApiResponse<Patient>> => {
    return post<Patient>('/patients', data);
  },

  updatePatient: (id: string, data: UpdatePatientDto): Promise<ApiResponse<Patient>> => {
    return patch<Patient>(`/patients/${id}`, data);
  },

  /**
   * Operational search returns active patients only. Report/history views can
   * opt in to inactive patients with `includeInactive`.
   */
  searchPatients: (query: string, includeInactive = false): Promise<ApiResponse<Patient[]>> => {
    const params = new URLSearchParams({ q: query });
    if (includeInactive) params.set('includeInactive', 'true');
    return get<Patient[]>(`/patients/search?${params.toString()}`);
  },

  checkDuplicate: (params: DuplicateCheckParams): Promise<ApiResponse<Patient[]>> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return get<Patient[]>(`/patients/check-duplicate?${searchParams.toString()}`);
  },

  deletePatient: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/patients/${id}`);
  },

  bulkDeletePatients: (ids: string[]): Promise<ApiResponse<{ deleted: string[] }>> => {
    return post<{ deleted: string[] }>('/patients/bulk-delete', { ids });
  },

  /** List soft-deleted patients (the Trash page). */
  getTrashedPatients: (page = 1, limit = 20, filters?: PatientListFilters): Promise<ApiResponse<Patient[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.gender) params.set('gender', filters.gender);
    if (filters?.bloodGroup) params.set('bloodGroup', filters.bloodGroup);
    if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
    return get<Patient[]>(`/patients/trash?${params.toString()}`) as Promise<ApiResponse<Patient[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed patients back to the active list. */
  restorePatients: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/patients/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed patients. */
  permanentlyDeletePatients: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/patients/trash/bulk-delete', { ids });
  },

  /**
   * Force-delete trashed patients together with every linked record. Used by the
   * "Delete Anyway" action when a normal permanent delete is blocked by foreign
   * keys. Irreversible.
   */
  forceDeletePatients: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/patients/trash/force-delete', { ids });
  },

  /**
   * Direct URL for the generated patient profile PDF. The endpoint responds with
   * `Content-Disposition: attachment`, so navigating to it starts a native
   * browser download.
   */
  getPatientPdfUrl: (id: string): string =>
    `${httpService.defaults.baseURL ?? ''}/patients/${id}/pdf`,
};
